import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnInit, Renderer2, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { GlobalVariables } from 'src/app/data/global-variables';
import { Permissions, TokenPermissions } from 'src/app/data/permissions';
import { AlertService } from 'src/app/services/alert.service';
import { BookService } from 'src/app/services/book.service';
import { Company, ConfigService } from 'src/app/services/config.service';
import { LoginService } from 'src/app/services/login.service';
import { PermissionService } from 'src/app/services/permission.service';
import { UserAccessService } from 'src/app/services/user-access.service';
import { AccessSelectionPopupComponent } from './access-selection-popup/access-selection-popup.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-access-locator',
  templateUrl: './access-locator.component.html',
  styleUrls: ['./access-locator.component.css']
})
export class AccessLocatorComponent implements OnInit {

  @ViewChild(AccessSelectionPopupComponent) accessSelectionPopup!: AccessSelectionPopupComponent;

  html_desktop: SafeHtml | undefined;
  html_mobile: SafeHtml | undefined;

  /** Indica si se debe mostrar el book en lugar del formulario de login */
  showBook = false;

  /** Flag para evitar que la suscripción a queryParams re-procese tras autenticación */
  private bookAuthenticated = false;

  // Propiedades de autenticación (migradas de check-access)
  token!: string;
  accessToken!: string;
  externalToken!: string;
  userName: string | null = null;
  password: string | null = null;
  showSpinner = false;

  locatorAccess = '';

  options: {
    headerPng: string,
    welcomeText: string,
    paragraph1: string,
    paragraph2: string,
  } | null = null;

  window = window;

  get company() {
    switch (this.configService.company) {
      case Company.DEBRAIN:
        return 'DeBrain';
      case Company.M3M:
        return 'M3M';
      default:
        return '';
    }
  }

  get accessWithLogin() {
    return this.configService?.config?.access_with_login;
  }

  get shareWithPrescriptor() {
    return this.configService?.config?.share_with_prescriptor;
  }

  constructor(
    private configService: ConfigService,
    private sanitizer: DomSanitizer,
    private _renderer2: Renderer2,
    @Inject(DOCUMENT) private _document: Document,
    public globalVariables: GlobalVariables,
    private alertService: AlertService,
    private loginService: LoginService,
    private userAccess: UserAccessService,
    private cookieService: CookieService,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    private bookService: BookService,
    private permissionService: PermissionService
  ) {
    // Leer parámetros de la URL para decidir el comportamiento
    this.route.queryParams.subscribe(params => {
      if (this.bookAuthenticated) return; // Ya autenticado, no reprocesar

      this.token = params['tkn'];
      this.accessToken = params['acctk'];
      this.externalToken = params['extkn'];

      if (this.accessToken || this.externalToken) {
        // No mostrar book directamente; el flujo de auth se ejecuta en ngOnInit
      } else if (this.token) {
        // Rellenar el locator con el valor del token
        this.prefillLocator(this.token);
      }
    });

    this.http.get<any>('assets/welcomePageResources/welcomePageOptions.json', { responseType: 'json' }).subscribe(data => {
      this.options = data;
    });

    this.http.get<string>('assets/welcomePageResources/login_desktop_locator.html', { responseType: 'string' as 'json' }).subscribe(
      next => {
        this.html_desktop = this.sanitizer.bypassSecurityTrustHtml(next);
      },
      error => {
        console.error('ERROR', error);
      });

    this.http.get<string>('assets/welcomePageResources/login_mobile_locator.html', { responseType: 'string' as 'json' }).subscribe(
      next => {
        this.html_mobile = this.sanitizer.bypassSecurityTrustHtml(next);
      },
      error => {
        console.error('ERROR', error);
      });

    // SCRIPTS QUE SON LLAMADOS DESDE EL HTML
    let script = this._renderer2.createElement('script');
    script.type = `text/javascript`;
    script.text = `
          function sendLogin() {
            let locator = document.getElementById('locator').value;
            let code = document.getElementById('code').value;
            let disclaimer = document.getElementById('disclaimer');
            if(disclaimer==null) {
              const event = new CustomEvent("login", { detail: {code,locator} });
              document.dispatchEvent(event);
            } else {
              if(disclaimer.checked) {
                const event = new CustomEvent("login", { detail: {code,locator} });
                document.dispatchEvent(event);
              } else {
                if(
                  document.getElementById('locator').value==null || document.getElementById('locator').value.trim()=='' ||
                  document.getElementById('code').value==null || document.getElementById('code').value.trim()==''
                ) {
                  alert('Introduzca todos los datos solicitados para acceder a sus resultados');
                } else {
                  alert('Debe aceptar los términos y condiciones para continuar');
                }
              }
            }
          }
        `;

    this._renderer2.appendChild(this._document.body, script);

    document.addEventListener("login", (evt: Event) => {
      const { code, locator } = (evt as CustomEvent).detail;
      console.log('code', code);
      console.log('locator', locator);
      this.checkAccess(locator, code);
    })
  }

  async ngOnInit(): Promise<void> {
    try {
      const systemConfig = await this.configService.getSystemConfig();
      this.locatorAccess = systemConfig.locatorAccess || '';
    } catch (err) {
      console.error('Error obteniendo system config', err);
    }

    // Verificar si hay sesión de paciente activa
    const patientSession = this.cookieService.get('patient_session');

    if (patientSession != null && patientSession != '' && this.token != null) {
      // Validar que el token pertenece al mismo paciente
      this.bookService.getPatientIdByToken(this.token).then(patientId => {
        if (patientId != null && patientId !== '' && patientId.toString() === patientSession) {
          // Mismo paciente, conceder acceso automático
          const storedAuthToken = localStorage.getItem('DBAuthTkn');
          if (storedAuthToken != null) {
            this.cookieService.delete('patient_session');
            this.bookService.getDefaultPermissions().then(permissions => {
              this.permissionService.permissions = new Set(Permissions.sanitizeAll(permissions));
              this.bookAuthenticated = true;
              this.showBook = true;
              this.changeDetectorRef.detectChanges();
              return;
            });
          }
        } else {
          // Si no coincide, eliminar cookie y continuar con flujo normal
          this.cookieService.delete('patient_session');
          this.continueNormalFlow();
        }
      });
    } else {
      this.continueNormalFlow();
    }
  }

  private continueNormalFlow() {
    if (environment.demo) {
      if ((environment as any).demoLaptop) {
        this.bookAuthenticated = true;
        this.showBook = true;
        this.changeDetectorRef.detectChanges();
      } else {
        if (this.accessToken != null) {
          this.userAccess.grantAccess(this.accessToken, this.token)
            .then(res => {
              this.bookAuthenticated = true;
              this.showBook = true;
              this.changeDetectorRef.detectChanges();
            })
            .catch(err => this.loginService.showError(err));
        }
      }
    } else {
      // Comprobar token de portal en la cookie
      const portalToken = this.cookieService.get('portal_token');
      if (portalToken != null && portalToken != '') {
        this.cookieService.delete('portal_token');
        this.userAccess.grantAccessFromCookies(portalToken)
          .then(res => {
            this.bookAuthenticated = true;
            this.showBook = true;
            this.changeDetectorRef.detectChanges();
          })
          .catch(err => console.error(err));
      }

      if (this.locatorAccess == 'locator' && this.token != null && this.token.trim() != '') {
        this.userAccess.grantAccessWithLocatorOnly(this.token).then(res => {
          this.bookService.getPatientIdByToken(this.token).then(patientId => {
            if (patientId != null && patientId !== '') {
              // Cookie válida por 2 horas
              this.cookieService.set('patient_session', patientId.toString(),
                undefined, '/', undefined, false, 'Strict');
            }
            this.bookAuthenticated = true;
            this.showBook = true;
            this.changeDetectorRef.detectChanges();
          });
        }).catch(err => console.error(err));
      }

      if (this.accessToken == null) {
        this.accessToken = this.cookieService.get('code');
        this.cookieService.delete('code');
      }
      if (this.accessToken != null && this.accessToken != '') {
        this.userAccess.grantAccess(this.accessToken, this.token)
          .then(res => {
            this.bookService.getPatientIdByToken(this.token).then(patientId => {
              if (patientId != null && patientId !== '') {
                // Cookie válida por 2 horas
                this.cookieService.set('patient_session', patientId.toString(),
                  undefined, '/', undefined, false, 'Strict');
              }
              this.bookAuthenticated = true;
              this.showBook = true;
              this.changeDetectorRef.detectChanges();
            });
          })
          .catch(err => console.error(err));
      }
      if (this.externalToken != null) {
        console.log('this.externalToken', this.externalToken);
        this.userAccess.grantAccessExternalToken(this.externalToken)
          .then(res => {
            this.token = res;
            // Actualizar la URL con el token real
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { tkn: res },
              replaceUrl: true
            }).then(() => {
              this.bookAuthenticated = true;
              this.showBook = true;
              this.changeDetectorRef.detectChanges();
            });
          })
          .catch(err => this.loginService.showError(err));
      }
    }
  }

  checkAccessWithUser() {
    this.userAccess.grantAccessWithUser(this.token, this.userName!, this.password!)
      .then(res => {
        this.bookAuthenticated = true;
        this.showBook = true;
        this.changeDetectorRef.detectChanges();
      })
      .catch(err => this.loginService.showError(err));
  }

  sendMail(mail: string) {
    if (mail == null || mail.trim() == '') {
      this.alertService.alert('Por favor, introduzca un correo electrónico');
    } else {
      if (!environment.demo) {
        if (this.configService._systemConfig?.prpSlave) {
          this.bookService.sendBookMailByPRP(mail, this.token).subscribe(res => {
            if (res) {
              this.alertService.alert('Correo enviado');
            } else {
              this.alertService.alert('Ha ocurrido un error al enviar el correo');
            }
          });
        } else {
          this.bookService.sendNotifToMail(mail, this.token).subscribe(res => {
            this.alertService.alert('Correo enviado');
          });
        }
      }
    }
  }

  /**
   * Almacena el valor a pre-rellenar e intenta rellenarlo de inmediato.
   */
  private prefillLocator(value: string) {
    this.bookService.getLocatorByToken(value).then(locator => {
      if (locator != null) {
        this.tryFillLocatorInput(locator);
      } else {
        console.error('No se pudo obtener el locator para el token proporcionado');
      }
    });
  }

  /**
   * Intenta rellenar el input #locator del template HTML inyectado via innerHTML.
   * Reintenta hasta 20 veces con 250ms de espera entre intentos.
   */
  private tryFillLocatorInput(value: string, retries = 20) {
    const locatorInput = document.getElementById('locator') as HTMLInputElement;
    if (locatorInput) {
      locatorInput.value = value;
    } else if (retries > 0) {
      setTimeout(() => this.tryFillLocatorInput(value, retries - 1), 250);
    }
  }

  private checkAccess(locator: string, code: string) {
    if (locator == null || locator.trim() == '') {
      this.alertService.alert(`Debe introducir el localizador de su estudio`);
      return;
    }

    console.log('code', code);
    this.showSpinner = true;
    this.userAccess.grantAccessWithLocator(locator, code)
      .then(res => {
        this.cookieService.set('code', code);

        // Marcar como autenticado para evitar reprocesamiento de queryParams
        this.bookAuthenticated = true;

        // Mostrar popup de selección de producto
        this.showSpinner = false;
        this.accessSelectionPopup.show(res.bookUrl || null, res.visorUrl || null, res.reportUrl || null);
      })
      .catch(err => {
        this.loginService.showError(err);
        this.showSpinner = false;
      })
  }

}
